import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Award, HelpCircle, Users, Megaphone, ArrowRight, Mail } from 'lucide-react';

/**
 * CustomSectionRenderer — Phase 66 Milestone 2.
 *
 * Renders a single landing-page custom section on the public coach page.
 * Each template handles its own layout, but all share the same
 * background style contract (bg_color, bg_image_url, text_color).
 *
 * The renderer accepts `theme` so multi-CTAs and accent buttons can
 * inherit the coach's chosen color scheme even when a section has its
 * own background. `onApply` opens the canonical Apply flow.
 *
 * NOTE: We never render raw HTML — coach-supplied text is interpolated
 * as text nodes only. Background image URLs are quoted in inline CSS
 * but never inserted into the DOM as elements.
 */

const FALLBACK_BG = '#ffffff';

function getSectionStyle(section) {
    const bgColor = section?.bg_color || '';
    const bgImage = section?.bg_image_url || '';
    const textColor = section?.text_color || 'dark';
    const style = {};
    if (bgImage) {
        // Dark overlay scaled by text mode so photos stay readable.
        const overlay = textColor === 'light'
            ? 'linear-gradient(rgba(10,22,40,0.62), rgba(10,22,40,0.62))'
            : 'linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7))';
        style.backgroundImage = `${overlay}, url("${bgImage}")`;
        style.backgroundSize = 'cover';
        style.backgroundPosition = 'center';
    } else if (bgColor) {
        style.background = bgColor;
    } else {
        style.background = FALLBACK_BG;
    }
    return style;
}

function getTextClasses(section) {
    const tc = section?.text_color || 'dark';
    return tc === 'light'
        ? { primary: 'text-white', secondary: 'text-white/90', muted: 'text-white/75' }
        : { primary: 'text-[#0a1628]', secondary: 'text-[#1a3a5c]', muted: 'text-[#1a3a5c]/80' };
}

// ---------------------------------------------------------------------------
// Wrapper used by every template — same padding, max width, vertical rhythm.
// ---------------------------------------------------------------------------
function SectionShell({ section, children, testId }) {
    return (
        <section
            className="relative overflow-hidden py-14 lg:py-20"
            style={getSectionStyle(section)}
            data-testid={testId || `custom-section-${section?.type}-${section?.id}`}
        >
            <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
                {children}
            </div>
        </section>
    );
}

// ============================================================================
// AWARDS
// ============================================================================
function AwardsSection({ section, headingClass }) {
    const d = section.data || {};
    const items = Array.isArray(d.items) ? d.items : [];
    const tc = getTextClasses(section);
    const showYear = d.show_year !== false;
    const layout = d.layout || 'grid';
    if (items.length === 0) return null;
    return (
        <SectionShell section={section}>
            <div className="text-center">
                {d.heading && <h2 className={`${headingClass} text-3xl ${tc.primary}`}>{d.heading}</h2>}
                {d.subheading && <p className={`mx-auto mt-3 max-w-2xl text-base ${tc.secondary}`}>{d.subheading}</p>}
            </div>
            {layout === 'logos_strip' ? (
                <div className="mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                    {items.map((it, i) => (
                        <div key={`${it.name || 'award'}-${i}`} className="flex flex-col items-center gap-2 text-center">
                            {it.logo_url ? (
                                <img src={it.logo_url} alt="" className="h-16 w-auto object-contain" loading="lazy" />
                            ) : (
                                <Award className={`h-10 w-10 ${tc.muted}`} />
                            )}
                            <div className={`text-xs font-semibold ${tc.primary}`}>{it.name}</div>
                            {showYear && it.year && <div className={`text-[11px] ${tc.muted}`}>{it.year}</div>}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((it, i) => (
                        <Card key={`${it.name || 'award'}-${i}`} className="border-0 bg-white/95 p-5 shadow-sm backdrop-blur">
                            <div className="flex items-start gap-3">
                                {it.logo_url ? (
                                    <img src={it.logo_url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain" loading="lazy" />
                                ) : (
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#ffd700]/20">
                                        <Award className="h-6 w-6 text-[#0a1628]" />
                                    </span>
                                )}
                                <div className="min-w-0">
                                    <div className="font-display text-base font-bold text-[#0a1628]">{it.name}</div>
                                    {it.issuer && <div className="text-sm text-[#1a3a5c]/80">{it.issuer}</div>}
                                    {showYear && it.year && <div className="mt-1 text-[11px] uppercase tracking-wider text-[#1a3a5c]/60">{it.year}</div>}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </SectionShell>
    );
}

// ============================================================================
// FAQ
// ============================================================================
function FaqSection({ section, headingClass, coach, accent, accentText }) {
    const { t: t42 } = useLocaleR42();
    const d = section.data || {};
    const items = Array.isArray(d.items) ? d.items : [];
    const tc = getTextClasses(section);
    const layout = d.layout || 'accordion';
    const showContactCta = d.show_contact_cta !== false;
    const contactHref = coach?.contact_email ? `mailto:${coach.contact_email}` : (coach?.contact_phone ? `tel:${(coach.contact_phone || '').replace(/[^\d+]/g, '')}` : '');
    if (items.length === 0) return null;
    return (
        <SectionShell section={section}>
            <div className="text-center">
                {d.heading && <h2 className={`${headingClass} text-3xl ${tc.primary}`}>{d.heading}</h2>}
                {d.subheading && <p className={`mx-auto mt-3 max-w-2xl text-base ${tc.secondary}`}>{d.subheading}</p>}
            </div>
            {layout === 'two_column' ? (
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    {items.map((it, i) => (
                        <Card key={`faq-${i}`} className="border-0 bg-white/95 p-5 shadow-sm backdrop-blur">
                            <div className="flex items-start gap-2">
                                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" />
                                <div className="min-w-0">
                                    <div className="font-display text-base font-bold text-[#0a1628]">{it.question}</div>
                                    <p className="mt-2 whitespace-pre-line text-sm text-[#1a3a5c]/85">{it.answer}</p>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="mx-auto mt-10 max-w-3xl rounded-2xl bg-white/95 p-2 shadow-sm backdrop-blur">
                    <Accordion type="single" collapsible className="w-full">
                        {items.map((it, i) => (
                            <AccordionItem key={`faq-${i}`} value={`faq-${i}`} className="border-[#0a1628]/10">
                                <AccordionTrigger className="px-4 text-left font-semibold text-[#0a1628] hover:no-underline">
                                    {it.question}
                                </AccordionTrigger>
                                <AccordionContent className="px-4 text-[#1a3a5c]/85">
                                    <p className="whitespace-pre-line text-sm leading-relaxed">{it.answer}</p>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            )}
            {showContactCta && contactHref && (
                <div className="mt-8 text-center">
                    <Button
                        asChild
                        className="sd-focus h-11 rounded-xl px-5 text-sm font-bold"
                        style={{ background: accent, color: accentText }}
                    >
                        <a href={contactHref}>
                            <Mail className="mr-1 h-4 w-4" /> {t42("Still have questions? Contact me")} </a>
                    </Button>
                </div>
            )}
        </SectionShell>
    );
}

// ============================================================================
// TEAM
// ============================================================================
function TeamSection({ section, headingClass }) {
    const d = section.data || {};
    const members = Array.isArray(d.members) ? d.members : [];
    const tc = getTextClasses(section);
    const showCerts = d.show_certifications !== false;
    const layout = d.layout || 'cards';
    if (members.length === 0) return null;
    return (
        <SectionShell section={section}>
            <div className="text-center">
                {d.heading && <h2 className={`${headingClass} text-3xl ${tc.primary}`}>{d.heading}</h2>}
                {d.subheading && <p className={`mx-auto mt-3 max-w-2xl text-base ${tc.secondary}`}>{d.subheading}</p>}
            </div>
            {layout === 'horizontal' ? (
                <div className="mt-10 flex gap-4 overflow-x-auto pb-4 sm:gap-6">
                    {members.map((m, i) => (
                        <div key={`m-${i}`} className="w-56 shrink-0 rounded-2xl bg-white/95 p-4 text-center shadow-sm backdrop-blur">
                            <div className="mx-auto h-24 w-24 overflow-hidden rounded-full bg-[#f4f7fb]">
                                {m.photo_url ? <img src={m.photo_url} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <Users className="m-auto mt-7 h-10 w-10 text-[#1a3a5c]/40" />}
                            </div>
                            <div className="mt-3 font-display text-sm font-bold text-[#0a1628]">{m.name}</div>
                            {m.role && <div className="text-xs text-[#1a3a5c]/80">{m.role}</div>}
                            {m.bio && <p className="mt-2 line-clamp-3 text-[11px] text-[#1a3a5c]/80">{m.bio}</p>}
                            {showCerts && m.certifications && <div className="mt-2 text-[10px] uppercase tracking-wider text-[#00d4ff]">{m.certifications}</div>}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {members.map((m, i) => (
                        <Card key={`m-${i}`} className="overflow-hidden border-0 bg-white/95 shadow-sm backdrop-blur">
                            <div className="aspect-[4/3] w-full bg-[#f4f7fb]">
                                {m.photo_url ? <img src={m.photo_url} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full items-center justify-center"><Users className="h-12 w-12 text-[#1a3a5c]/30" /></div>}
                            </div>
                            <div className="p-4">
                                <div className="font-display text-lg font-bold text-[#0a1628]">{m.name}</div>
                                {m.role && <div className="text-sm font-semibold text-[#1a3a5c]/85">{m.role}</div>}
                                {m.bio && <p className="mt-2 text-sm leading-relaxed text-[#1a3a5c]/80">{m.bio}</p>}
                                {showCerts && m.certifications && (
                                    <div className="mt-3 inline-flex flex-wrap items-center gap-1 rounded-full bg-[#e6fbff] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0a1628]">
                                        <Award className="h-3 w-3" /> {m.certifications}
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </SectionShell>
    );
}

// ============================================================================
// PROMO BANNER
// ============================================================================
function PromoBannerSection({ section, onApply, accent, accentText }) {
    const d = section.data || {};
    const tc = getTextClasses(section);
    const showIcon = d.show_icon !== false;
    const onClick = (e) => {
        if (!d.cta_link) {
            // No explicit link → open the Apply flow.
            e.preventDefault();
            onApply();
        }
    };
    if (!d.headline && !d.subheadline && !d.cta_text) return null;
    return (
        <section
            className="relative overflow-hidden py-8 lg:py-10"
            style={getSectionStyle(section)}
            data-testid={`custom-section-promo_banner-${section.id}`}
        >
            <div className="relative z-10 mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3">
                    {showIcon && (
                        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tc.primary === 'text-white' ? 'bg-white/15' : 'bg-[#0a1628]/10'}`}>
                            <Megaphone className={`h-5 w-5 ${tc.primary}`} />
                        </span>
                    )}
                    <div className="min-w-0">
                        {d.headline && <div className={`font-display text-lg font-bold ${tc.primary}`}>{d.headline}</div>}
                        {d.subheadline && <div className={`mt-0.5 text-sm ${tc.secondary}`}>{d.subheadline}</div>}
                    </div>
                </div>
                {d.cta_text && (
                    <a
                        href={d.cta_link || '#apply'}
                        onClick={onClick}
                        target={d.cta_link ? '_blank' : undefined}
                        rel={d.cta_link ? 'noreferrer' : undefined}
                        className={`sd-focus inline-flex h-11 shrink-0 items-center justify-center gap-1 rounded-xl px-5 text-sm font-bold shadow-sm transition ${
                            (d.cta_style || 'solid') === 'outline'
                                ? `border-2 ${tc.primary === 'text-white' ? 'border-white text-white hover:bg-white/10' : 'border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628]/5'}`
                                : ''
                        }`}
                        style={(d.cta_style || 'solid') === 'outline' ? undefined : { background: accent, color: accentText }}
                        data-testid={`custom-promo-cta-${section.id}`}
                    >
                        {d.cta_text} <ArrowRight className="h-4 w-4" />
                    </a>
                )}
            </div>
        </section>
    );
}

// ============================================================================
// CTA STRIP
// ============================================================================
function CtaStripSection({ section, onApply, accent, accentText, headingClass }) {
    const d = section.data || {};
    const tc = getTextClasses(section);
    const alignment = d.alignment || 'center';
    const alignClass = alignment === 'left' ? 'text-left items-start' : alignment === 'right' ? 'text-right items-end' : 'text-center items-center';
    const primaryText = d.primary_cta_text || 'Apply for Lessons';
    if (!d.headline && !primaryText) return null;
    return (
        <SectionShell section={section} testId={`custom-section-cta_strip-${section.id}`}>
            <div className={`mx-auto flex max-w-3xl flex-col gap-3 ${alignClass}`}>
                {d.headline && <h2 className={`${headingClass} text-3xl ${tc.primary}`}>{d.headline}</h2>}
                {d.subheadline && <p className={`max-w-2xl text-base ${tc.secondary}`}>{d.subheadline}</p>}
                <div className={`mt-2 flex flex-wrap gap-3 ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                    <Button
                        onClick={() => onApply()}
                        className="sd-focus h-12 rounded-xl px-6 text-base font-bold shadow-lg"
                        style={{ background: accent, color: accentText }}
                        data-testid={`custom-cta-primary-${section.id}`}
                    >
                        {primaryText} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                    {d.secondary_cta_text && (
                        <Button
                            asChild
                            variant="outline"
                            className={`h-12 rounded-xl border-2 px-5 text-base font-bold ${tc.primary === 'text-white' ? 'border-white text-white hover:bg-white/10' : 'border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628]/5'}`}
                        >
                            <a
                                href={d.secondary_cta_link || '#'}
                                target={d.secondary_cta_link?.startsWith('http') ? '_blank' : undefined}
                                rel={d.secondary_cta_link?.startsWith('http') ? 'noreferrer' : undefined}
                                data-testid={`custom-cta-secondary-${section.id}`}
                            >
                                {d.secondary_cta_text}
                            </a>
                        </Button>
                    )}
                </div>
            </div>
        </SectionShell>
    );
}

// ============================================================================
// Main switchboard
// ============================================================================
export default function CustomSectionRenderer({ section, coach, accent, accentText, headingClass, onApply }) {
    if (!section || !section.type) return null;
    switch (section.type) {
        case 'awards':
            return <AwardsSection section={section} headingClass={headingClass} />;
        case 'faq':
            return <FaqSection section={section} headingClass={headingClass} coach={coach} accent={accent} accentText={accentText} />;
        case 'team':
            return <TeamSection section={section} headingClass={headingClass} />;
        case 'promo_banner':
            return <PromoBannerSection section={section} onApply={onApply} accent={accent} accentText={accentText} />;
        case 'cta_strip':
            return <CtaStripSection section={section} onApply={onApply} accent={accent} accentText={accentText} headingClass={headingClass} />;
        default:
            return null;
    }
}
